import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";

import type { Expense } from "./expenseTypes";

type SetReimbursedVariables = {
  expenses: Expense[];
  /** `YYYY-MM-DD` to mark as reimbursed; null to unmark. */
  reimbursedAt: string | null;
};

/**
 * Marks (with a date) or unmarks one or several expenses as reimbursed. Only
 * the two reimbursement columns are sent; the data layer audit-logs each
 * update and rejects anything but an uncancelled Pepe Diaz expense.
 */
export const useSetReimbursed = (onDone?: () => void) => {
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const translate = useTranslate();
  return useMutation({
    mutationFn: ({ expenses, reimbursedAt }: SetReimbursedVariables) =>
      Promise.all(
        expenses.map((expense) =>
          dataProvider.update("expenses", {
            id: expense.id,
            data: {
              reimbursed: reimbursedAt != null,
              reimbursed_at: reimbursedAt,
            },
            previousData: expense,
          }),
        ),
      ),
    onSuccess: (_result, { expenses, reimbursedAt }) => {
      notify(
        reimbursedAt
          ? "crm.expenses.reimbursement.marked"
          : "crm.expenses.reimbursement.unmarked",
        { messageArgs: { smart_count: expenses.length } },
      );
      void queryClient.invalidateQueries({ queryKey: ["expenses"] });
      onDone?.();
    },
    onError: (error) => {
      notify(
        error instanceof Error && error.message
          ? error.message
          : translate("crm.expenses.save_error"),
        { type: "error" },
      );
      console.error("setReimbursed.error", error);
    },
  });
};
